import { createContext, ReactNode, useCallback, useMemo } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useTranslation } from 'react-i18next';
import fetchData, { resolveIsLegacyLogin } from '@/utils/fetchData';
import { getProcessEnvUrl } from '@/utils/getUrl';

type Props = {
  children: ReactNode;
};

type LoginResponse = {
  authorizationToken: string;
  fromTemporaryPassword: boolean;
  accessVerificationRequired?: boolean;
};

export type AuthContextType = {
  postAccessVerification: (
    code: string,
    encodedCredentials: string,
  ) => Promise<LoginResponse>;
  resend: (encodedCredentials: string) => void;
};

export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType,
);

export function AuthProvider({ children }: Props): React.JSX.Element {
  const { t } = useTranslation('error');

  const resend = useCallback(async (encodedCredentials: string) => {
    const path = '/token/basic';
    await fetchData(path, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Basic ${encodedCredentials}`,
      },
    });
  }, []);

  const postAccessVerification = useCallback(
    async (accessCode: string, encodedCredentials: string) => {
      const path = '/token/access-verification';
      const resolvedIsLegacyLogin = await resolveIsLegacyLogin();
      const baseUrl = resolvedIsLegacyLogin
        ? 'LEGACY_LOGIN_API'
        : 'WEB_API_URL';
      const url = `${getProcessEnvUrl(baseUrl)}${path}`;

      const res = await fetch(url, {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${encodedCredentials}`,
        },
        body: `accessCode=${accessCode}`,
      });

      if (!res.ok) {
        try {
          const data = await res.json();

          datadogLogs.logger.error(`request: ${path}`, {
            request_url: url,
            error: data,
          });
        } catch (exception) {
          datadogLogs.logger.error(`request: ${path} response parse`, {
            request_url: url,
            error: exception,
          });
        }

        if (res.status === 400 || res.status === 401) {
          throw new Error(t('validation.incorrect_code'));
        }

        if (res.status === 406) {
          throw new Error(t('token_expired'));
        }

        if (res.status === 429) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          throw new Error(t('validation.too_many_attempts'));
        }

        throw new Error(t('generic'));
      }

      const data = await res.json();

      const authorizationToken = btoa(`${data.username}:${data.token}`);
      const { fromTemporaryPassword } = data;

      sessionStorage.setItem('token', authorizationToken);
      sessionStorage.setItem('fromTemporaryPassword', fromTemporaryPassword);

      return {
        authorizationToken,
        fromTemporaryPassword,
      };
    },
    [],
  );

  const profileContextValue = useMemo(
    () => ({
      postAccessVerification,
      resend,
    }),
    [postAccessVerification, resend],
  );

  return (
    <AuthContext.Provider value={profileContextValue}>
      {children}
    </AuthContext.Provider>
  );
}
